import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Keyboard } from 'react-native';

import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { InputAccessoryView } from '@/components/ui/input-accessory-view';

/**
 * Ties an accessory bar to the field it sits above. Derived from the field name
 * so two questions on one page cannot claim the same bar.
 */
export function getSurveyAccessoryViewId(fieldName: string) {
  return `${fieldName}-keyboard-done`;
}

interface SurveyKeyboardDoneBarProps {
  fieldName: string;
}

/**
 * A Done bar above the keyboard, for the survey fields whose keyboard has no
 * return key to dismiss it: a multiline answer, where Return inserts a newline,
 * and the numeric and phone pads, which iOS draws without one at all.
 *
 * Without it the only ways out are scrolling or tapping elsewhere on the page,
 * and tapping elsewhere is the problem — the scroll views here persist taps, so
 * a tap aimed at blank space to close the keyboard is delivered to whatever
 * control it lands on, and a survey page is mostly radio and checkbox rows that
 * are 48px tall to meet the touch-target minimum. A respondent dismissing the
 * keyboard could silently change an answer they had already given. An explicit
 * Done means they never have to tap a control to get the keyboard out of the way.
 *
 * iOS only, and deliberately so rather than by oversight: Android dismisses with
 * the system back button and web has no on-screen keyboard to dismiss. React
 * Native's own component renders nothing off iOS, so the guard here is about not
 * mounting it on web rather than about correctness.
 */
export function SurveyKeyboardDoneBar({ fieldName }: SurveyKeyboardDoneBarProps) {
  // react-native-web does not implement this component at all, so the shared
  // primitive re-exports `undefined` there. Rendering that throws rather than
  // degrading, and the platform flag alone does not catch it: the form tests
  // report native while still rendering through react-native-web.
  if (isWeb || !InputAccessoryView) {
    return null;
  }

  return (
    <InputAccessoryView nativeID={getSurveyAccessoryViewId(fieldName)}>
      <HStack className="justify-end bg-background-0 px-3 py-2">
        <Button size="sm" onPress={() => Keyboard.dismiss()}>
          <ButtonText>Done</ButtonText>
        </Button>
      </HStack>
    </InputAccessoryView>
  );
}
